import React, { useState } from "react";
import { Button, Modal, Input, message, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import styled from "styled-components";
import { Warning } from "@phosphor-icons/react";
import { colors, spacing } from "../../../styles/theme";
import { getCompany, getCurrentUser, deleteAccount, getAdminCount } from "../../../service/client";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const WarningBox = styled.div`
  display: flex;
  gap: ${spacing.md};
  padding: ${spacing.lg};
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
  margin-top: ${spacing.md};
`;

const WarningIcon = styled.div`
  flex-shrink: 0;
  color: #f57c00;
`;

const WarningContent = styled.div`
  flex: 1;
`;

const WarningTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #e65100;
  margin-bottom: ${spacing.xs};
`;

const WarningText = styled.div`
  font-size: 13px;
  color: #e65100;
  line-height: 1.5;
  
  ul {
    margin: ${spacing.sm} 0 0 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
`;

const DangerButton = styled(Button)`
  background: ${colors.error} !important;
  border-color: ${colors.error} !important;
  
  &:hover {
    background: #d32f2f !important;
    border-color: #d32f2f !important;
  }
`;

const ConfirmationText = styled.p`
  margin: ${spacing.md} 0;
  font-size: 14px;
  color: ${colors.textPrimary};
`;

const StyledInput = styled(Input)`
  margin-top: ${spacing.sm};
`;

interface DeleteAccountProps {
    onAccountDeleted?: () => void;
}

export const DeleteAccount: React.FC<DeleteAccountProps> = ({ onAccountDeleted }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const queryClient = useQueryClient();

    const { data: currentUser } = useQuery(["/admin/current_user"], () => getCurrentUser());
    const { data: company } = useQuery(["/company"], () => getCompany());

    // Check if user is the only admin
    const { data: adminCount } = useQuery(
        ["/admin/count"],
        () => getAdminCount(),
        {
            enabled: currentUser?.role === "ADMIN",
        }
    );

    const deleteAccountMutation = useMutation(
        () => deleteAccount(),
        {
            onSuccess: () => {
                message.success(t("deleteAccount.accountDeletedSuccess"));
                queryClient.clear();
                if (onAccountDeleted) {
                    onAccountDeleted();
                }
                // Redirect to account deleted page
                window.location.href = "/account-deleted";
            },
            onError: (error: any) => {
                message.error(error?.response?.data?.error || t("deleteAccount.accountDeleteError"));
            },
        }
    );

    const handleDeleteClick = () => {
        setIsModalVisible(true);
        setConfirmText("");
    };

    const handleConfirmDelete = () => {
        if (confirmText.trim().toLowerCase() !== t("deleteAccount.confirmWord").toLowerCase()) {
            message.error(t("deleteAccount.confirmTextError"));
            return;
        }
        deleteAccountMutation.mutate();
    };

    const isOnlyAdmin = currentUser?.role === "ADMIN" && adminCount?.isOnlyAdmin;

    return (
        <Container>
            <Section>
                <SectionTitle>{t("deleteAccount.sectionTitle")}</SectionTitle>
                <SectionDescription>
                    {t("deleteAccount.sectionDescription")}
                </SectionDescription>

                {isOnlyAdmin && company && (
                    <WarningBox>
                        <WarningIcon>
                            <Warning size={24} weight="fill" />
                        </WarningIcon>
                        <WarningContent>
                            <WarningTitle>{t("deleteAccount.warningTitle")}</WarningTitle>
                            <WarningText>
                                {t("deleteAccount.warningIntro")}
                                <ul>
                                    <li><strong>{company.name}</strong> {t("deleteAccount.warningItemCompany")}</li>
                                    <li>{t("deleteAccount.warningItemBranches")}</li>
                                    <li>{t("deleteAccount.warningItemProducts")}</li>
                                    <li>{t("deleteAccount.warningItemOrders")}</li>
                                    <li>{t("deleteAccount.warningItemCustomers")}</li>
                                </ul>
                                {t("deleteAccount.warningOutroBefore")} <strong>{t("deleteAccount.warningOutroBold")}</strong> {t("deleteAccount.warningOutroAfter")}
                            </WarningText>
                        </WarningContent>
                    </WarningBox>
                )}

                <div>
                    <DangerButton type="primary" danger onClick={handleDeleteClick}>
                        {t("deleteAccount.deleteButton")}
                    </DangerButton>
                </div>
            </Section>

            <Modal
                title={t("deleteAccount.modalTitle")}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={handleConfirmDelete}
                okText={t("deleteAccount.modalOkText")}
                cancelText={t("deleteAccount.modalCancelText")}
                okButtonProps={{
                    danger: true,
                    disabled: confirmText.trim().toLowerCase() !== t("deleteAccount.confirmWord").toLowerCase(),
                    loading: deleteAccountMutation.isLoading,
                }}
            >
                {isOnlyAdmin && company ? (
                    <>
                        <Alert
                            message={t("deleteAccount.alertWarningTitle")}
                            description={
                                <>
                                    {t("deleteAccount.alertDescriptionBefore")} <strong>{company.name}</strong>.
                                    {" "}{t("deleteAccount.alertDescriptionAfter")}
                                </>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: spacing.lg }}
                        />
                        <ConfirmationText>
                            {t("deleteAccount.confirmWithCompanyBefore")} <strong>{company.name}</strong>{t("deleteAccount.confirmWithCompanyMiddle")} <strong>{t("deleteAccount.confirmWord")}</strong> {t("deleteAccount.confirmWithCompanyAfter")}
                        </ConfirmationText>
                    </>
                ) : (
                    <ConfirmationText>
                        {t("deleteAccount.confirmBefore")} <strong>{t("deleteAccount.confirmWord")}</strong> {t("deleteAccount.confirmAfter")}
                    </ConfirmationText>
                )}

                <StyledInput
                    placeholder={t("deleteAccount.inputPlaceholder")}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    onPressEnter={handleConfirmDelete}
                />
            </Modal>
        </Container>
    );
};
